import { view } from "@risingstack/react-easy-state";
import { useEffect, useMemo } from "react";
import { type Product, legalFilterKeys } from "../../common/types/productTypes";
import useMediaQuery from "../../common/useMediaQuery";
import { desktopBreakpoint } from "../../common/utils";
import featuresStore from "../../stores/features/featuresStore";
import productStore from "../../stores/product/productStore";
import { buildFilter } from "../../stores/product/productUtils";
import ShowMoreContainer from "../common/ShowMoreContainer";
import CurrentFilters from "./CurrentFilters";
import FilterChips from "./FilterChips";
import FilterSelectors from "./FilterSelectors";

type FilterSectionProps = {
   products: Product[];
   categoryKey?: string;
};
const chipFilterResetFields = legalFilterKeys.filter((key) => key !== "brandGroup" && key !== "format");

const FilterSection = ({ products, categoryKey }: FilterSectionProps) => {
   const isDesktop = useMediaQuery(desktopBreakpoint);
   const { filterDisplayThreshold } = featuresStore;
   const currentBrandProducts = useMemo(
      () =>
         productStore.activeFilters.brandGroup?.length
            ? products.filter((p) => productStore.activeFilters.brandGroup?.includes(p.brandGroup))
            : products,
      [products, productStore.activeFilters?.brandGroup]
   );

   const brandFilterSettings = useMemo(() => buildFilter(products, "brandGroup", "brandGroup"), [products]);
   const formatFilterSettings = useMemo(() => buildFilter(currentBrandProducts, "format", "format"), [currentBrandProducts]);

   useEffect(() => {
      if (!brandFilterSettings) {
         productStore.clearFilterFields(["brandGroup"]);
      }
      if (!formatFilterSettings) {
         productStore.clearFilterFields(["format"]);
      }
   }, [brandFilterSettings, formatFilterSettings]);

   const { navigationTestVariant } = featuresStore;

   const filterChipsContainerStyle = "tw-my-4";

   if (navigationTestVariant === "megamenu" || products.length <= filterDisplayThreshold) {
      return null;
   }
   return (
      <>
         {categoryKey === "ost" && !!formatFilterSettings && (
            <div className={filterChipsContainerStyle}>
               <h3 className="tw-text-h5">Format</h3>
               <ShowMoreContainer rows={isDesktop ? 1 : 2} gap={2}>
                  <FilterChips
                     filterSettings={formatFilterSettings}
                     exclusive={true}
                     includeAll={true}
                     resetFilterFields={chipFilterResetFields}
                  />
               </ShowMoreContainer>
            </div>
         )}
         {!!brandFilterSettings && brandFilterSettings.values.length > 1 && (
            <div className={filterChipsContainerStyle}>
               <h3 className="tw-text-h5">Merkevarer</h3>
               <ShowMoreContainer rows={isDesktop ? 1 : 2} gap={2}>
                  <FilterChips
                     filterSettings={brandFilterSettings}
                     exclusive={true}
                     includeAll={true}
                     resetFilterFields={chipFilterResetFields}
                  />
               </ShowMoreContainer>
            </div>
         )}
         <div className="tw-my-4">
            <FilterSelectors products={products} variant={categoryKey} />
         </div>
         <div className="tw-my-4">
            <CurrentFilters ignoreFields={["brandGroup", "format"]} />
         </div>
      </>
   );
};

export default view(FilterSection);
